import { useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { useDates } from "dates/DatesContext";
import { useFetch } from "hooks/api";
import useSelectedMetrics from "components/custom/merchandise/hooks/useSelectedMetrics";
import MultiMetricChart from "components/custom/merchandise/MultiMetricChart";
import Loading from "components/core/blocks/Loading";
import { buildMetric } from "views/merchandise/metrics";
import DetailedMetric from "components/core/blocks/DetailedMetric";
import moment from "moment";
import { keepPreviousData } from "@tanstack/react-query";
import { useAcosOrRoas } from "../../utils/acosOrRoas";

const OrgPerformanceExpander = ({ row, allOrgs = false }) => {
  const acosOrRoas = useAcosOrRoas();

  const { org_id: orgId, org_name: orgName, market_place: marketPlace } = row._original ?? {};

  const { group, start, end, preStart, preEnd } = useDates();

  const filters = {
    mp: marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    empty: true,
    org: orgId,
    all_active_orgs: allOrgs,
  };

  const { data, isLoading } = useFetch(
    ["product_ad_report", start, end, preStart, preEnd, group, orgId],
    `/api/data_report/org_report`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      enabled: !!orgId,
    },
  );

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    ["sales", "ad_sales", "ad_spend", "ctr"],
    "orgPerformanceExpander",
  );

  const metrics = useMemo(
    () => [
      buildMetric(marketPlace, data, "sales", "sales_percent"),
      buildMetric(marketPlace, data, "units", "units_percent"),
      buildMetric(marketPlace, data, "margin", "margin_delta"),
      acosOrRoas(
        buildMetric(marketPlace, data, "acos", "acos_delta"),
        buildMetric(marketPlace, data, "roas", "roas_delta"),
      ),
      buildMetric(marketPlace, data, "ad_sales", "ad_sales_percent"),
      buildMetric(marketPlace, data, "ad_spend", "ad_spend_percent"),
      buildMetric(marketPlace, data, "roms", "roms_delta"),
      buildMetric(marketPlace, data, "cost", "cost_percent"),
      buildMetric(marketPlace, data, "promo_cost", "promo_cost_percent"),
    ],
    [marketPlace, data, acosOrRoas],
  );

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  const dateFilterWithoutToday = data?.date?.filter((d) => moment().format("YYYY-MM-DD") !== d.start_date) ?? [];

  if (isLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <div className="bg-white p-4">
      <Row>
        <Col xs={12}>
          <div style={{ display: "flex" }}>
            <div>
              <Row>
                <Col xs={12}>
                  <h3
                    style={{
                      marginBottom: "0.2rem",
                      marginTop: "0",
                      fontSize: 22,
                    }}
                  >
                    {orgName}
                  </h3>
                </Col>
              </Row>
            </div>
          </div>
        </Col>

        <Col xs={12}>
          <MultiMetricChart
            metrics={metrics}
            selectedMetrics={selectedMetrics}
            data={dateFilterWithoutToday}
            group={group}
            handleLegendChange={handleLegendChange}
            datesFormatted={true}
            showAnnotations={false}
            showAll
          />
        </Col>
        <Col xs={12}>
          <Row className="pt-4">
            {metrics?.slice(0, 6)?.map((m, i) => {
              return (
                <Col xs={12} md={6} lg={2} key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`}>
                  <div className="">
                    <div>
                      <DetailedMetric
                        center
                        size={"small"}
                        id={m.id}
                        toolTip={m.toolTip}
                        name={m.name}
                        value={m.overrideValue ?? m.actual}
                        target={m.target}
                        formatter={m.formatter}
                        targetFormatter={m?.targetFormatter}
                        showTarget={false}
                        targetLabel={m?.targetLabel}
                        targetColor={m.target > 0 ? "#007000" : "#d2222d"}
                      />
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default OrgPerformanceExpander;
